import type { FC } from 'react';

import Pill from '@/common-components/Pill';
import { formatRupees } from '@/utils/currency.util';
import type { AdminBookingListItem } from '@/types/rental.types';

import { CATEGORY_META } from '@/constants/listing.constants';

import { BOOKING_STATUS_LABEL } from '../constants';

interface ActiveRentalsTableProps {
  bookings: AdminBookingListItem[];
}

const formatShortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const th =
  'px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-[var(--color-ink-muted)]';
const td = 'px-4 py-3.5 text-[13px] text-[var(--color-ink)]';

const ActiveRentalsTable: FC<ActiveRentalsTableProps> = ({ bookings }) => (
  <>
    {/* Desktop table */}
    <div className="hidden overflow-x-auto md:block">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)]">
            <th className={th}>Booking</th>
            <th className={th}>Owner → Borrower</th>
            <th className={th}>Campus</th>
            <th className={th}>Period</th>
            <th className={th}>Rental</th>
            <th className={th}>Deposit</th>
            <th className={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking) => {
            const status = BOOKING_STATUS_LABEL[booking.status];
            const meta = CATEGORY_META[booking.listing.category];
            return (
              <tr
                key={booking.id}
                className="border-b border-[var(--color-divider)] transition-colors last:border-b-0 hover:bg-[var(--color-surface-muted)]"
              >
                <td className={td}>
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg">{meta.icon}</span>
                    <div>
                      <div className="font-semibold">{booking.listing.title}</div>
                      <div className="font-mono text-[11px] text-[var(--color-ink-soft)]">
                        #{booking.code}
                      </div>
                    </div>
                  </div>
                </td>
                <td className={td}>
                  <span className="font-medium">{booking.ownerName}</span>
                  <span className="text-[var(--color-ink-soft)]"> → </span>
                  <span className="font-medium">{booking.borrowerName}</span>
                </td>
                <td className={td}>{booking.campusName}</td>
                <td className={td}>
                  {formatShortDate(booking.period.startDate)} →{' '}
                  {formatShortDate(booking.period.endDate)}
                  <span className="text-[11px] text-[var(--color-ink-muted)]">
                    {' '}
                    · {booking.period.days}d
                  </span>
                </td>
                <td className={`${td} font-semibold`}>{formatRupees(booking.pricing.rentalPaise)}</td>
                <td className={td}>
                  {booking.pricing.depositPaise > 0
                    ? formatRupees(booking.pricing.depositPaise)
                    : '—'}
                </td>
                <td className={td}>
                  <Pill tone={status.tone}>{status.label}</Pill>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    {/* Mobile cards */}
    <div className="flex flex-col gap-3 p-4 md:hidden">
      {bookings.map((booking) => {
        const status = BOOKING_STATUS_LABEL[booking.status];
        const meta = CATEGORY_META[booking.listing.category];
        return (
          <div
            key={booking.id}
            className="rounded-xl border border-[var(--color-divider)] bg-white p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <span className="text-xl">{meta.icon}</span>
                <div>
                  <div className="text-[13px] font-semibold text-[var(--color-ink)]">
                    {booking.listing.title}
                  </div>
                  <div className="font-mono text-[11px] text-[var(--color-ink-soft)]">
                    #{booking.code}
                  </div>
                </div>
              </div>
              <Pill tone={status.tone}>{status.label}</Pill>
            </div>
            <div className="mt-2.5 text-[12px] text-[var(--color-ink-muted)]">
              {booking.ownerName} → {booking.borrowerName} · {booking.campusName}
            </div>
            <div className="mt-1 text-[12px] text-[var(--color-ink-muted)]">
              {formatShortDate(booking.period.startDate)} →{' '}
              {formatShortDate(booking.period.endDate)} · {booking.period.days}d ·{' '}
              <span className="font-semibold text-[var(--color-ink)]">
                {formatRupees(booking.pricing.rentalPaise)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  </>
);

export default ActiveRentalsTable;
